// src/components/settings/UserSettings.tsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  Table,
  TableRow,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as ManagerIcon,
  Person as PersonIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import { usersAPI, UserDto } from '../../services/api';
import { useErrorHandler } from '../../errors';
import { useGlobalToast } from '../../App';
import SettingsHeaderRow from './shared/SettingsHeaderRow';
import {
  AdminChip,
  ManagerChip,
  StaffChip,
  SettingsChip,
  ConfirmDeleteDialog,
} from './shared/SettingsElements';
import SettingsTableActions from './shared/SettingsTableActions';
import {
  StyledTableContainer,
  StyledTableHead,
  StyledTableHeaderCell,
  StyledTableBody,
  StyledTableRow,
  StyledTableCell,
  StyledTableChipCell,
  StyledTableChipCellContent,
  StyledTableActionsCell,
} from './shared/SettingsTableElements';
import UserDialog from './shared/dialogs/UserDialog';
import LoadingSpinner from '../common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';

type UserRole = 'Admin' | 'Manager' | 'Staff';

const UsersSettings: React.FC = () => {
  const [users, setUsers] = useState<UserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [searchText, setSearchText] = useState('');
  const [dialogError, setDialogError] = useState<string | null>(null);
  const { handleError } = useErrorHandler();
  const { showSuccess } = useGlobalToast();
  const { user: currentUser } = useAuth();

  const isSelf = (user: UserDto) => currentUser?.id === user.id;

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setUsers(await usersAPI.getAll());
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [handleError]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = useMemo(() => {
    if (!searchText) return users;
    const lower = searchText.toLowerCase();
    return users.filter(
      (u) =>
        u.username.toLowerCase().includes(lower) ||
        u.email.toLowerCase().includes(lower) ||
        u.role.toLowerCase().includes(lower)
    );
  }, [users, searchText]);

  const handleOpenDialog = (user?: UserDto) => {
    setEditingUser(user ?? null);
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleSave = async (formData: {
    username: string;
    email: string;
    password: string;
    role: UserRole;
  }) => {
    try {
      setDialogError(null);
      if (editingUser) {
        const payload: any = {
          username: formData.username,
          email: formData.email,
          role: formData.role,
        };
        if (formData.password) payload.password = formData.password;
        await usersAPI.update(editingUser.id, payload);
        showSuccess();
      } else {
        await usersAPI.create(formData);
        showSuccess();
      }
      await fetchUsers();
      setDialogOpen(false);
    } catch (error: any) {
      const message =
        typeof error?.response?.data === 'string'
          ? error.response.data
          : error?.response?.data?.message ||
            error?.message ||
            'An error occurred';
      setDialogError(message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await usersAPI.delete(deleteTarget.id);
      showSuccess();
      await fetchUsers();
    } catch (error) {
      handleError(error);
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleToggleActive = async (user: UserDto) => {
    try {
      await usersAPI.update(user.id, { isActive: !user.isActive });
      await fetchUsers();
    } catch (error) {
      handleError(error);
    }
  };

  const handleUnlock = async (user: UserDto) => {
    try {
      await usersAPI.unlock(user.id);
      showSuccess();
      await fetchUsers();
    } catch (error) {
      handleError(error);
    }
  };

  const isLockedOut = (user: UserDto) =>
    !!user.lockoutUntil && new Date(user.lockoutUntil) > new Date();

  const getRoleIcon = (role: string) => {
    if (role === 'Admin')
      return <AdminIcon fontSize="medium" color="primary" />;
    if (role === 'Manager')
      return <ManagerIcon fontSize="medium" color="secondary" />;
    return <PersonIcon fontSize="medium" />;
  };

  const getRoleChip = (role: string) => {
    if (role === 'Admin') return <AdminChip />;
    if (role === 'Manager') return <ManagerChip />;
    return <StaffChip />;
  };

  if (loading) return <LoadingSpinner message="Loading Users..." size={80} />;

  return (
    <Box>
      <SettingsHeaderRow
        title="Manage Users"
        onAdd={() => handleOpenDialog()}
        addLabel="Add User"
        onSearch={setSearchText}
        onClearSearch={() => setSearchText('')}
        searchText={searchText}
        searchPlaceholder="Search users..."
        buttonMinWidth="212px"
      />

      {users.length === 0 ? (
        <Alert severity="info">
          No users found. Click "Add User" to create one.
        </Alert>
      ) : filteredUsers.length === 0 ? (
        <Alert severity="info">No users match "{searchText}".</Alert>
      ) : (
        <StyledTableContainer>
          <Table>
            <StyledTableHead>
              <TableRow>
                <StyledTableHeaderCell>Username</StyledTableHeaderCell>
                <StyledTableHeaderCell>Email</StyledTableHeaderCell>
                <StyledTableHeaderCell align="center">
                  Role
                </StyledTableHeaderCell>
                <StyledTableHeaderCell align="center">
                  Status
                </StyledTableHeaderCell>
                <StyledTableHeaderCell align="right">
                  Actions
                </StyledTableHeaderCell>
              </TableRow>
            </StyledTableHead>
            <StyledTableBody>
              {filteredUsers.map((user, index) => (
                <StyledTableRow key={user.id} isOdd={index % 2 === 0}>
                  <StyledTableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getRoleIcon(user.role)}
                      <Typography fontWeight={600}>{user.username}</Typography>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography>{user.email}</Typography>
                  </StyledTableCell>
                  <StyledTableChipCell>
                    <StyledTableChipCellContent>
                      {getRoleChip(user.role)}
                    </StyledTableChipCellContent>
                  </StyledTableChipCell>
                  <StyledTableChipCell>
                    <StyledTableChipCellContent>
                      <SettingsChip>
                        {isSelf(user) ? (
                          <Tooltip title="You cannot change your own status">
                            <span>
                              <Chip
                                label={user.isActive ? 'Active' : 'Inactive'}
                                size="small"
                                color={user.isActive ? 'success' : 'default'}
                                disabled
                                sx={{ padding: '2.4rem 0.25rem' }}
                              />
                            </span>
                          </Tooltip>
                        ) : (
                          <Chip
                            label={user.isActive ? 'Active' : 'Inactive'}
                            size="small"
                            color={user.isActive ? 'success' : 'default'}
                            onClick={() => handleToggleActive(user)}
                            sx={{
                              cursor: 'pointer',
                              padding: '2.4rem 0.25rem',
                            }}
                          />
                        )}
                      </SettingsChip>
                      {isLockedOut(user) && (
                        <SettingsChip>
                          {isSelf(user) ? (
                            <Tooltip title="You cannot unlock your own account">
                              <span>
                                <Chip
                                  label="Locked"
                                  size="small"
                                  color="error"
                                  icon={<LockIcon />}
                                  disabled
                                  sx={{
                                    padding: '2.4rem 0.25rem',
                                    marginLeft: '2rem',
                                  }}
                                />
                              </span>
                            </Tooltip>
                          ) : (
                            <Chip
                              label="Locked"
                              size="small"
                              color="error"
                              icon={<LockIcon />}
                              onClick={() => handleUnlock(user)}
                              sx={{
                                cursor: 'pointer',
                                padding: '2.4rem 0.25rem',
                                marginLeft: '2rem',
                              }}
                            />
                          )}
                        </SettingsChip>
                      )}
                    </StyledTableChipCellContent>
                  </StyledTableChipCell>
                  <StyledTableActionsCell align="right">
                    <SettingsTableActions
                      itemId={user.id}
                      itemName={user.username}
                      refCount={0}
                      onEdit={() => handleOpenDialog(user)}
                      onDelete={() =>
                        setDeleteTarget({ id: user.id, name: user.username })
                      }
                    />
                  </StyledTableActionsCell>
                </StyledTableRow>
              ))}
            </StyledTableBody>
          </Table>
        </StyledTableContainer>
      )}

      <UserDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setDialogError(null);
        }}
        onSave={handleSave}
        editing={editingUser}
        errorMessage={dialogError}
      />

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        title="Delete user?"
        message={`Are you sure you want to delete "${deleteTarget?.name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
};

export default UsersSettings;
